/*
  Carousel — rebuild in place instead of tearing down.

  ---------- The bug ----------

  When a variant change hides or shows gallery images, ProductMedia in theme.js
  calls FlickityCarousel.reload(). That method destroys Flickity and creates a
  new instance asynchronously. For a frame or so in between, the slides fall
  back to their un-initialised layout and the gallery collapses to a fraction
  of its height.

  On mobile the gallery sits above the variant picker, so everything below it,
  including the button the shopper just tapped, moves up by the height that
  was lost. The browser's scroll anchoring then compensates and moves back. It
  looks like the page jumps to Add to Cart and returns.

  ---------- The fix ----------

  Flickity can re-read its cells without being destroyed. reloadCells() picks
  up the new visible set (via cellSelector, which skips .is-filtered), and
  select() + resize() put it on the right slide at the right height, all
  synchronously. The gallery never collapses.

  Patched on the prototype rather than in theme.js, which is a minified build
  artefact. Applies to every carousel on the store, which is intended: the
  collapse happened on the live PDP too.
*/

(function () {
  'use strict';

  function patch(FlickityCarousel) {
    var proto = FlickityCarousel.prototype;
    if (proto.__reloadInPlace) return;
    proto.__reloadInPlace = true;

    var originalReload = proto.reload;

    /*
      One rebuild per frame, however many times it is asked for.

      A variant change on the Madison template asks twice: theme.js first,
      after un-hiding every image it cannot filter (all 36), then the color
      and size filter once it has hidden them again. Rebuilding for each meant
      two passes, the first over 36 slides only to be thrown away: about 120ms
      per tap in WebKit, against 68ms on a PDP that rebuilds once or not at all.

      Deferring to the next animation frame collapses the two into one, and it
      runs against the final state because the filter settles in a microtask,
      which always comes first. Animation frames run before paint, so nothing
      is ever drawn in between.
    */
    proto.reload = function () {
      var self = this;
      var args = arguments;

      if (!self.__pendingReload) {
        self.__pendingReload = new Promise(function (resolve) {
          window.requestAnimationFrame(function () {
            self.__pendingReload = null;
            resolve(self.__rebuildInPlace(args));
          });
        });
      }

      return self.__pendingReload;
    };

    proto.__rebuildInPlace = async function (args) {
      var flkty = await this.flickity;

      // Not built yet, or not something we recognise: fall back to the theme.
      if (!flkty || typeof flkty.reloadCells !== 'function' || !flkty.slider) {
        return originalReload.apply(this, args);
      }

      var config = this.flickityConfig;

      // Flickity only moves the cells matching cellSelector into its slider
      // when it is built, so anything filtered out at that moment is still a
      // direct child of the host, outside the viewport. Left there, a slide
      // that the filter now shows renders as a full image stacked under the
      // carousel. Gather both sets into the slider, in original order, and
      // let reloadCells pick out the visible ones. This is the same reordering
      // the theme's reload does, applied where Flickity keeps the cells.
      //
      // Only when something is actually out of place: re-inserting every
      // slide on every rebuild restyles all of them for nothing.
      if (config.cellSelector) {
        var position = function (node) {
          return parseInt(node.getAttribute('data-original-position'), 10);
        };

        var stray = Array.from(this.children).filter(function (node) {
          return node.hasAttribute('data-original-position');
        });

        var inSlider = Array.from(flkty.slider.children);
        var ordered = inSlider.every(function (node, i) {
          return i === 0 || position(inSlider[i - 1]) <= position(node);
        });

        if (stray.length || !ordered) {
          inSlider
            .concat(stray)
            .sort(function (a, b) { return position(a) - position(b); })
            .forEach(function (node) { flkty.slider.appendChild(node); });
        }
      }

      flkty.reloadCells();

      // The caller marks the slide to land on with .is-initial-selected, the
      // same marker the theme's reload feeds to Flickity's initialIndex.
      var target = this.querySelector('.is-initial-selected:not(.is-filtered)');
      var index = 0;
      if (target) {
        flkty.cells.some(function (cell, i) {
          if (cell.element === target) { index = i; return true; }
          return false;
        });
      }

      flkty.select(index, false, true);
      flkty.resize();

      // Listeners that the theme wired to 'ready' (slide counter, thumbnail
      // highlight, video autoplay, image preload) expect to hear about the
      // rebuilt carousel, as they did after a full rebuild.
      this.dispatchEvent(new CustomEvent('flickity:ready', { bubbles: true, detail: {} }));
    };
  }

  window.customElements.whenDefined('flickity-carousel').then(function () {
    patch(window.customElements.get('flickity-carousel'));
  });
})();
